import { PRESERVE_SCROLL_STATE } from '../hooks/scroll/usePaneScrollRestoration';

/**
 * The history-entry key that says "the assistant sheet is open on this entry".
 *
 * Below `md` the assistant is a sheet over the page, and whether it is open is
 * read from `location.state` rather than held in memory, so Back, Forward, and
 * every in-app navigation move it the way a phone user expects: Back closes
 * it, a chip click leaves it, Back from that record page returns to it.
 *
 * The value records how the entry came to carry the key. `pushed` — the app
 * pushed it from a live entry, so closing can pop back; `replaced` — the app
 * landed here by redirect (a `/assistant` or `/alerts` deep link), where there
 * is no in-app predecessor to pop to, so closing must replace the state.
 */
export const ASSISTANT_SHEET_STATE_KEY = 'assistantSheet';

/**
 * The key an entry carries while the assistant is on the `/assistant` route
 * after a sheet open crossed `md`: the sheet's origin, held so that narrowing
 * back restores an entry closing can pop rather than one it must rewrite.
 */
export const ASSISTANT_SHEET_HANDOFF_KEY = 'assistantSheetHandoff';

export type AssistantSheetEntryOrigin = 'pushed' | 'replaced';

const isStateObject = (state: unknown): state is Record<string, unknown> => typeof state === 'object' && state !== null;

/** How an entry carrying the sheet came to carry it, or `null` when it does not. */
export const getAssistantSheetOrigin = (state: unknown): AssistantSheetEntryOrigin | null => {
  if (!isStateObject(state)) {
    return null;
  }
  const origin = state[ASSISTANT_SHEET_STATE_KEY];
  return origin === 'pushed' || origin === 'replaced' ? origin : null;
};

export const isAssistantSheetEntry = (state: unknown): boolean => getAssistantSheetOrigin(state) !== null;

/**
 * The state a sheet-opening entry carries. Spreads the entry's existing state
 * so another writer's keys survive, and carries `PRESERVE_SCROLL_STATE` so the
 * page under the sheet keeps its scroll position instead of resetting to the
 * top (`usePaneScrollRestoration` treats any other push as a new page).
 *
 * The contract runs the other way too: a `navigate(…, { state })` writer that
 * does not spread `location.state` drops this key and closes the sheet by
 * derivation. That is correct for a navigation to a new page, and the reason
 * same-page view-state writes must spread.
 */
export const withAssistantSheet = (state: unknown, origin: AssistantSheetEntryOrigin): Record<string, unknown> => ({
  ...(isStateObject(state) ? state : {}),
  ...PRESERVE_SCROLL_STATE,
  [ASSISTANT_SHEET_STATE_KEY]: origin,
});

/**
 * The same entry with the sheet closed. Keeps `preserveScroll`: closing by
 * replace is a view-state write, and without it the page would scroll to the
 * top as the sheet slides away.
 */
export const withoutAssistantSheet = (state: unknown): Record<string, unknown> => {
  const rest: Record<string, unknown> = { ...(isStateObject(state) ? state : {}) };
  delete rest[ASSISTANT_SHEET_STATE_KEY];
  delete rest[ASSISTANT_SHEET_HANDOFF_KEY];
  return { ...rest, ...PRESERVE_SCROLL_STATE };
};

/** The entry the route takes over from an open sheet: the sheet key gone, its origin held for the way back. */
export const withAssistantSheetHandoff = (
  state: unknown,
  origin: AssistantSheetEntryOrigin
): Record<string, unknown> => ({
  ...withoutAssistantSheet(state),
  [ASSISTANT_SHEET_HANDOFF_KEY]: origin,
});

/** The origin a route entry is holding for the sheet, or `null` when it holds none. */
export const getAssistantSheetHandoff = (state: unknown): AssistantSheetEntryOrigin | null => {
  if (!isStateObject(state)) {
    return null;
  }
  const origin = state[ASSISTANT_SHEET_HANDOFF_KEY];
  return origin === 'pushed' || origin === 'replaced' ? origin : null;
};
